import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    ArrowRightIcon,
    BuildingOffice2Icon,
    MapPinIcon,
} from "@heroicons/react/24/outline";
import Hero from "../components/Hero";
import destImg from "../assests/images/hotel-bg.jpg";

export default function DestinationsList({
    showBanner = true,
    showHeading = true,
}) {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    // =========================================================
    // FETCH DESTINATIONS
    // =========================================================

    const fetchDestinations = async () => {
        setLoading(true);

        try {
            const res = await axios.get(
                "https://marinadubai.com/api/destinations"
            );

            setDestinations(res.data);
        } catch (err) {
            console.error("Failed to fetch destinations:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDestinations();
    }, []);

    return (
        <div className="w-full bg-[#F7F5EF]">

            {/* =========================================================
                HERO
            ========================================================= */}

            {showBanner && (
                <Hero
                    variant="inner"
                    title="Explore Dubai"
                    subtitle="Discover exceptional stays across Dubai's most sought-after destinations"
                    backgroundImage={destImg}
                />
            )}

            {/* =========================================================
                MAIN CONTENT
            ========================================================= */}

            <section
                className="
                    relative
                    overflow-hidden
                    bg-[#F7F5EF]
                    py-14
                    sm:py-16
                    lg:py-20
                "
            >

                {/* =====================================================
                    SUBTLE BACKGROUND DETAILS
                ===================================================== */}

                <div
                    className="
                        pointer-events-none
                        absolute
                        -left-40
                        top-20
                        h-80
                        w-80
                        rounded-full
                        bg-[#D9E7E4]/50
                        blur-3xl
                    "
                />

                <div
                    className="
                        pointer-events-none
                        absolute
                        -right-40
                        bottom-10
                        h-80
                        w-80
                        rounded-full
                        bg-[#D5A33C]/10
                        blur-3xl
                    "
                />

                <div
                    className="
                        relative
                        mx-auto
                        max-w-[1440px]
                        px-5
                        sm:px-8
                        lg:px-12
                        xl:px-16
                    "
                >

                    {/* =====================================================
                        SECTION INTRO
                    ===================================================== */}

                    {showHeading && (
                        <div
                            className="
                                mb-10
                                flex
                                flex-col
                                gap-6

                                lg:mb-12
                                lg:flex-row
                                lg:items-end
                                lg:justify-between
                            "
                        >

                            {/* LEFT CONTENT */}

                            <div className="max-w-[650px]">

                                {/* Eyebrow */}

                                <div
                                    className="
                                        mb-4
                                        flex
                                        items-center
                                        gap-3
                                    "
                                >
                                    <span
                                        className="
                                            h-[1px]
                                            w-8
                                            bg-[#D5A33C]
                                        "
                                    />

                                    <span
                                        className="
                                            text-[10px]
                                            font-semibold
                                            uppercase
                                            tracking-[0.22em]
                                            text-[#0B5552]
                                        "
                                    >
                                        Discover Dubai
                                    </span>
                                </div>

                                {/* Heading */}

                                <h2
                                    className="
                                        font-serif
                                        text-[30px]
                                        font-medium
                                        leading-[1.08]
                                        tracking-[-0.02em]
                                        text-[#173F3E]

                                        sm:text-[36px]

                                        lg:text-[44px]
                                    "
                                >
                                    Find your perfect
                                    <span
                                        className="
                                            block
                                            italic
                                            text-[#B78932]
                                        "
                                    >
                                        Dubai destination.
                                    </span>
                                </h2>

                                {/* Description */}

                                <p
                                    className="
                                        mt-4
                                        max-w-[580px]
                                        text-[12px]
                                        leading-6
                                        text-[#687572]

                                        sm:text-[13px]
                                    "
                                >
                                    Explore Dubai's most popular areas and
                                    discover beautiful stays that match the
                                    way you want to experience the city.
                                </p>
                            </div>

                            {/* DESTINATION COUNT */}

                            {!loading && destinations.length > 0 && (
                                <div className="shrink-0">

                                    <div
                                        className="
                                            flex
                                            items-center
                                            gap-4
                                            rounded-[16px]
                                            border
                                            border-[#DED8CC]
                                            bg-[#FBFAF6]
                                            px-5
                                            py-4
                                            shadow-[0_8px_25px_rgba(15,45,44,0.05)]
                                        "
                                    >

                                        <div
                                            className="
                                                flex
                                                h-11
                                                w-11
                                                items-center
                                                justify-center
                                                rounded-full
                                                border
                                                border-[#D5A33C]/40
                                                bg-[#F3EBDD]
                                            "
                                        >
                                            <MapPinIcon
                                                className="
                                                    h-5
                                                    w-5
                                                    text-[#0B5552]
                                                "
                                            />
                                        </div>

                                        <div>
                                            <p
                                                className="
                                                    font-serif
                                                    text-2xl
                                                    font-medium
                                                    leading-none
                                                    text-[#173F3E]
                                                "
                                            >
                                                {destinations.length}
                                            </p>

                                            <p
                                                className="
                                                    mt-1
                                                    text-[9px]
                                                    font-semibold
                                                    uppercase
                                                    tracking-[0.15em]
                                                    text-[#687572]
                                                "
                                            >
                                                Destinations
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* =====================================================
                        GOLD DIVIDER
                    ===================================================== */}

                    {showHeading && (
                        <div className="mb-8 flex items-center gap-3">
                            <span className="h-[1px] w-12 bg-[#D5A33C]" />

                            <span className="h-1 w-1 rounded-full bg-[#D5A33C]" />

                            <span className="h-[1px] w-4 bg-[#D5A33C]/40" />
                        </div>
                    )}

                    {/* =====================================================
                        LOADING SKELETON
                    ===================================================== */}

                    {loading ? (
                        <div
                            className="
                                grid
                                grid-cols-1
                                gap-5

                                sm:grid-cols-2

                                lg:grid-cols-12
                            "
                        >

                            {/* Featured Skeleton */}

                            <div
                                className="
                                    h-[390px]
                                    animate-pulse
                                    overflow-hidden
                                    rounded-[20px]
                                    bg-[#E7DED0]
                                    sm:col-span-2
                                    lg:col-span-7
                                "
                            />

                            {/* Smaller Skeletons */}

                            <div
                                className="
                                    h-[185px]
                                    animate-pulse
                                    rounded-[20px]
                                    bg-[#E7DED0]
                                    lg:col-span-5
                                "
                            />

                            <div
                                className="
                                    h-[185px]
                                    animate-pulse
                                    rounded-[20px]
                                    bg-[#E7DED0]
                                    lg:col-span-5
                                "
                            />

                            <div
                                className="
                                    h-[185px]
                                    animate-pulse
                                    rounded-[20px]
                                    bg-[#E7DED0]
                                    lg:col-span-4
                                "
                            />

                            <div
                                className="
                                    h-[185px]
                                    animate-pulse
                                    rounded-[20px]
                                    bg-[#E7DED0]
                                    lg:col-span-4
                                "
                            />

                            <div
                                className="
                                    h-[185px]
                                    animate-pulse
                                    rounded-[20px]
                                    bg-[#E7DED0]
                                    lg:col-span-4
                                "
                            />
                        </div>
                    ) : destinations.length === 0 ? (

                        /* =================================================
                           EMPTY STATE
                        ================================================= */

                        <div
                            className="
                                rounded-[20px]
                                border
                                border-[#DED8CC]
                                bg-[#FBFAF6]
                                px-6
                                py-16
                                text-center
                                shadow-[0_10px_30px_rgba(15,45,44,0.05)]
                            "
                        >

                            <div
                                className="
                                    mx-auto
                                    flex
                                    h-16
                                    w-16
                                    items-center
                                    justify-center
                                    rounded-full
                                    border
                                    border-[#D5A33C]/40
                                    bg-[#F3EBDD]
                                "
                            >
                                <MapPinIcon
                                    className="
                                        h-7
                                        w-7
                                        text-[#0B5552]
                                    "
                                />
                            </div>

                            <h3
                                className="
                                    mt-5
                                    font-serif
                                    text-xl
                                    font-medium
                                    text-[#173F3E]
                                "
                            >
                                No destinations available
                            </h3>

                            <p
                                className="
                                    mx-auto
                                    mt-2
                                    max-w-md
                                    text-[12px]
                                    leading-6
                                    text-[#687572]
                                "
                            >
                                We couldn't find any destinations right now.
                                Please check again shortly.
                            </p>
                        </div>

                    ) : (

                        /* =================================================
                           DESTINATION GRID
                        ================================================= */

                        <div
                            className="
                                grid
                                grid-cols-1
                                gap-5

                                sm:grid-cols-2

                                lg:grid-cols-12
                            "
                        >

                            {destinations.map((dest, index) => {

                                const isFeatured = index === 0;

                                return (
                                    <div
                                        key={dest.id}
                                        onClick={() =>
                                            navigate(
                                                `/destinations/${dest.slug}`
                                            )
                                        }
                                        className={`
                                            group
                                            relative
                                            cursor-pointer
                                            overflow-hidden
                                            rounded-[20px]
                                            bg-[#D9E1DE]
                                            shadow-[0_10px_30px_rgba(15,45,44,0.08)]
                                            transition-all
                                            duration-500
                                            hover:-translate-y-1
                                            hover:shadow-[0_20px_45px_rgba(15,45,44,0.16)]

                                            ${isFeatured
                                                ? "h-[390px] sm:col-span-2 lg:col-span-7"
                                                : "h-[250px] lg:col-span-5"
                                            }
                                        `}
                                    >

                                        {/* =================================================
                                            IMAGE
                                        ================================================= */}

                                        <img
                                            src={`https://uaediscountedhotels.com/uploads/destinations/${dest.image}`}
                                            alt={dest.name}
                                            loading={
                                                index > 2
                                                    ? "lazy"
                                                    : "eager"
                                            }
                                            className="
                                                absolute
                                                inset-0
                                                h-full
                                                w-full
                                                object-cover
                                                transition-transform
                                                duration-700
                                                ease-out
                                                group-hover:scale-110
                                            "
                                        />

                                        {/* =================================================
                                            IMAGE OVERLAY
                                        ================================================= */}

                                        <div
                                            className="
                                                absolute
                                                inset-0
                                                bg-gradient-to-t
                                                from-[#092F2E]/95
                                                via-[#0B5552]/35
                                                to-transparent
                                                transition-all
                                                duration-500

                                                group-hover:from-[#073F3D]
                                                group-hover:via-[#0B5552]/45
                                            "
                                        />

                                        {/* =================================================
                                            SUBTLE GOLD GLOW
                                        ================================================= */}

                                        <div
                                            className="
                                                absolute
                                                inset-0
                                                bg-gradient-to-br
                                                from-[#D5A33C]/0
                                                via-transparent
                                                to-[#D5A33C]/25
                                                opacity-0
                                                transition-opacity
                                                duration-500
                                                group-hover:opacity-100
                                            "
                                        />

                                        {/* =================================================
                                            TOP LEFT NUMBER
                                        ================================================= */}

                                        <div
                                            className="
                                                absolute
                                                left-4
                                                top-4

                                                sm:left-5
                                                sm:top-5
                                            "
                                        >
                                            <div
                                                className="
                                                    flex
                                                    h-9
                                                    min-w-9
                                                    items-center
                                                    justify-center
                                                    rounded-full
                                                    border
                                                    border-white/25
                                                    bg-[#173F3E]/75
                                                    px-2
                                                    font-serif
                                                    text-xs
                                                    italic
                                                    text-white
                                                    shadow-lg
                                                    backdrop-blur-md
                                                "
                                            >
                                                {String(index + 1).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </div>
                                        </div>

                                        {/* =================================================
                                            HOTEL COUNT
                                        ================================================= */}

                                        <div
                                            className="
                                                absolute
                                                right-4
                                                top-4

                                                sm:right-5
                                                sm:top-5
                                            "
                                        >
                                            <div
                                                className="
                                                    flex
                                                    h-8
                                                    items-center
                                                    gap-1.5
                                                    rounded-full
                                                    border
                                                    border-white/25
                                                    bg-white/15
                                                    px-3
                                                    shadow-lg
                                                    backdrop-blur-md
                                                "
                                            >
                                                <BuildingOffice2Icon
                                                    className="
                                                        h-3.5
                                                        w-3.5
                                                        text-[#E5C56D]
                                                    "
                                                />

                                                <span
                                                    className="
                                                        text-[10px]
                                                        font-semibold
                                                        text-white

                                                        sm:text-xs
                                                    "
                                                >
                                                    {dest.hotels_count || 0}{" "}
                                                    Hotels
                                                </span>
                                            </div>
                                        </div>

                                        {/* =================================================
                                            CONTENT
                                        ================================================= */}

                                        <div
                                            className="
                                                absolute
                                                inset-x-0
                                                bottom-0
                                                p-5

                                                sm:p-6
                                            "
                                        >

                                            <div
                                                className="
                                                    flex
                                                    items-end
                                                    justify-between
                                                    gap-4
                                                "
                                            >

                                                {/* TEXT */}

                                                <div className="min-w-0">

                                                    <div
                                                        className="
                                                            mb-2
                                                            flex
                                                            items-center
                                                            gap-2
                                                        "
                                                    >
                                                        <span
                                                            className="
                                                                h-[1px]
                                                                w-7
                                                                bg-[#E5C56D]
                                                                transition-all
                                                                duration-500
                                                                group-hover:w-11
                                                            "
                                                        />

                                                        <span
                                                            className="
                                                                text-[9px]
                                                                font-semibold
                                                                uppercase
                                                                tracking-[0.2em]
                                                                text-[#E5C56D]
                                                            "
                                                        >
                                                            Dubai
                                                        </span>
                                                    </div>

                                                    <h3
                                                        className={`
                                                            truncate
                                                            font-serif
                                                            font-medium
                                                            tracking-[-0.01em]
                                                            text-white
                                                            ${isFeatured
                                                                ? "text-2xl sm:text-3xl lg:text-4xl"
                                                                : "text-xl sm:text-2xl"
                                                            }
                                                        `}
                                                    >
                                                        {dest.name}
                                                    </h3>

                                                    <p
                                                        className="
                                                            mt-1.5
                                                            text-[11px]
                                                            text-white/70

                                                            sm:text-xs
                                                        "
                                                    >
                                                        Explore hotels and
                                                        beautiful stays
                                                    </p>
                                                </div>

                                                {/* =================================================
                                                    ARROW
                                                ================================================= */}

                                                <div
                                                    className="
                                                        flex
                                                        h-11
                                                        w-11
                                                        shrink-0
                                                        items-center
                                                        justify-center
                                                        rounded-full
                                                        bg-[#FBFAF6]
                                                        text-[#0B5552]
                                                        shadow-xl
                                                        transition-all
                                                        duration-300

                                                        group-hover:bg-[#D5A33C]
                                                        group-hover:text-[#173F3E]
                                                        group-hover:rotate-[-45deg]
                                                    "
                                                >
                                                    <ArrowRightIcon
                                                        className="h-5 w-5"
                                                    />
                                                </div>
                                            </div>

                                            {/* =================================================
                                                BOTTOM ACCENT
                                            ================================================= */}

                                            <div
                                                className="
                                                    mt-5
                                                    h-[1px]
                                                    w-full
                                                    overflow-hidden
                                                    rounded-full
                                                    bg-white/20
                                                "
                                            >
                                                <div
                                                    className="
                                                        h-full
                                                        w-0
                                                        rounded-full
                                                        bg-[#D5A33C]
                                                        transition-all
                                                        duration-500
                                                        group-hover:w-full
                                                    "
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* =====================================================
                        BOTTOM CTA
                    ===================================================== */}

                    {!loading && destinations.length > 0 && (
                        <div
                            className="
                                mt-10
                                overflow-hidden
                                rounded-[20px]
                                border
                                border-[#DED8CC]
                                bg-[#FBFAF6]
                                shadow-[0_10px_30px_rgba(15,45,44,0.05)]
                            "
                        >

                            <div
                                className="
                                    flex
                                    flex-col
                                    gap-5
                                    px-6
                                    py-6

                                    sm:px-8
                                    sm:py-7

                                    md:flex-row
                                    md:items-center
                                    md:justify-between
                                "
                            >

                                {/* LEFT */}

                                <div
                                    className="
                                        flex
                                        items-center
                                        gap-4
                                    "
                                >

                                    <div
                                        className="
                                            hidden
                                            h-12
                                            w-12
                                            shrink-0
                                            items-center
                                            justify-center
                                            rounded-full
                                            border
                                            border-[#D5A33C]/40
                                            bg-[#F3EBDD]

                                            sm:flex
                                        "
                                    >
                                        <MapPinIcon
                                            className="
                                                h-5
                                                w-5
                                                text-[#0B5552]
                                            "
                                        />
                                    </div>

                                    <div>

                                        <h3
                                            className="
                                                font-serif
                                                text-base
                                                font-medium
                                                text-[#173F3E]

                                                sm:text-lg
                                            "
                                        >
                                            Ready to explore Dubai?
                                        </h3>

                                        <p
                                            className="
                                                mt-1
                                                text-[11px]
                                                leading-5
                                                text-[#687572]

                                                sm:text-xs
                                            "
                                        >
                                            Find the perfect hotel for your
                                            next Dubai stay.
                                        </p>
                                    </div>
                                </div>

                                {/* BUTTON */}

                                <button
                                    type="button"
                                    onClick={() => navigate("/hotels")}
                                    className="
                                        inline-flex
                                        items-center
                                        justify-center
                                        gap-2
                                        rounded-full
                                        bg-[#0B5552]
                                        px-6
                                        py-3
                                        text-[11px]
                                        font-semibold
                                        uppercase
                                        tracking-[0.08em]
                                        text-white
                                        shadow-[0_8px_20px_rgba(11,85,82,0.18)]
                                        transition-all
                                        duration-300
                                        hover:-translate-y-0.5
                                        hover:bg-[#073F3D]
                                        hover:shadow-[0_12px_25px_rgba(11,85,82,0.25)]
                                    "
                                >
                                    Browse Hotels

                                    <ArrowRightIcon
                                        className="h-4 w-4"
                                    />
                                </button>
                            </div>

                            {/* =================================================
                                GOLD BOTTOM LINE
                            ================================================= */}

                            <div
                                className="
                                    h-[2px]
                                    w-full
                                    bg-gradient-to-r
                                    from-[#0B5552]
                                    via-[#D5A33C]
                                    to-[#0B5552]
                                "
                            />
                        </div>
                    )}

                    {/* =====================================================
                        BOTTOM BRAND MESSAGE
                    ===================================================== */}

                    {!loading && destinations.length > 0 && (
                        <div
                            className="
                                mt-7
                                flex
                                flex-col
                                items-center
                                justify-center
                                gap-2
                                text-center

                                sm:flex-row
                                sm:gap-3
                            "
                        >
                            <span
                                className="
                                    h-1.5
                                    w-1.5
                                    rounded-full
                                    bg-[#D5A33C]
                                "
                            />

                            <span
                                className="
                                    text-[9px]
                                    font-medium
                                    uppercase
                                    tracking-[0.18em]
                                    text-[#687572]
                                "
                            >
                                Discover Dubai · Stay beautifully · Travel
                                confidently
                            </span>

                            <span
                                className="
                                    h-1.5
                                    w-1.5
                                    rounded-full
                                    bg-[#D5A33C]
                                "
                            />
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}